/**
 * Phase 5 batch 1 Slice 2 — Firestore-backed AnswerPersistenceAdapter.
 *
 * Implements the contract from
 * `packages/form-engine/src/persistence/adapter.ts` against a Firestore
 * `Firestore` instance using the **modular** Firebase JS SDK v10.
 *
 * Document shape (per Phase 5 plan §"Firestore document layout"):
 *
 *   applications/{applicationId}
 *     ownerUserId: string                  -- written by linkage layer
 *     schemaVersion: string
 *     status: 'draft' | 'submitted' | 'archived'
 *     createdAt: Timestamp                 -- set by linkage on create
 *     updatedAt: Timestamp                 -- set by adapter on each save
 *     savedAt: string                      -- ISO-8601 mirror of snapshot.savedAt
 *     answers: { [fieldKey]: SerializedAnswer }
 *
 * Where `fieldKey`:
 *
 *   `${fieldId}` for non-repeatable singletons, or
 *   `${fieldId}::${groupInstanceId}` for repeatable groups, or
 *   `${fieldId}::${groupInstanceId}::${partyId}` when partyId disambiguates.
 *
 * The map shape is per ADR 0011's snapshot model (single-doc write per
 * save, single-doc read per load). The adapter performs the
 * array<->map transformation; the engine still sees a `readonly Answer[]`.
 *
 * Schema-drift handling: per ADR 0012, a stored `schemaVersion` that
 * does not match `LoadOptions.expectedSchemaVersion` produces a
 * non-blocking `LoadWarning`. The adapter never coerces or rejects.
 *
 * Source-cited Firebase APIs (verified against
 * `node_modules/@firebase/firestore/dist/index.d.ts`):
 *   - `doc(firestore, 'applications', appId)` → DocumentReference
 *   - `getDoc(ref)` → DocumentSnapshot
 *   - `setDoc(ref, data)` → Promise<void> (full-document write)
 *   - `onSnapshot(ref, observer)` → Unsubscribe
 *   - `DocumentSnapshot.metadata.hasPendingWrites: boolean` (filters
 *     local-write echoes per Phase 5 plan risk note)
 *
 * https://firebase.google.com/docs/firestore/quickstart
 * https://firebase.google.com/docs/firestore/manage-data/add-data
 * https://firebase.google.com/docs/firestore/query-data/listen
 */

import type { Answer, AnswerValue, FieldId } from '@inside-us-aos/domain';
import type {
  AnswerPersistenceAdapter,
  LoadOptions,
  LoadWarning,
  PersistedLoadResult,
  PersistedSnapshot,
  PersistenceListener,
  Unsubscribe,
} from '@inside-us-aos/form-engine';
import {
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type Firestore,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';

const APPLICATIONS_COLLECTION = 'applications';

interface SerializedAnswer {
  applicationId: string;
  fieldId: string;
  partyId?: string;
  groupInstanceId?: string;
  value: AnswerValue;
  schemaVersion: string;
  updatedAt: string;
  updatedBy: string;
}

interface SerializedApplicationDoc {
  schemaVersion: string;
  savedAt: string;
  /** ISO-8601 mirror of the most recent server-side write. */
  updatedAt?: string;
  /** Map of fieldKey -> serialized answer. */
  answers?: Record<string, SerializedAnswer>;
  /** Set by the linkage layer (Slice 4); the adapter does not write it. */
  ownerUserId?: string;
  /** Set on create by the linkage layer; the adapter does not modify it. */
  createdAt?: unknown;
  /** Set on create by the linkage layer; the adapter does not modify it. */
  status?: 'draft' | 'submitted' | 'archived';
}

const fieldKeyOf = (answer: Answer): string => {
  const groupPart = answer.groupInstanceId ?? '';
  const partyPart = answer.partyId ?? '';
  if (!groupPart && !partyPart) return answer.fieldId;
  if (!partyPart) return `${answer.fieldId}::${groupPart}`;
  return `${answer.fieldId}::${groupPart}::${partyPart}`;
};

const serializeAnswer = (answer: Answer): SerializedAnswer => {
  const out: SerializedAnswer = {
    applicationId: answer.applicationId,
    fieldId: answer.fieldId,
    value: answer.value,
    schemaVersion: answer.schemaVersion,
    updatedAt: answer.updatedAt,
    updatedBy: answer.updatedBy,
  };
  // Firestore rejects `undefined` field values; only emit optional keys
  // when they are defined.
  if (answer.partyId !== undefined) out.partyId = answer.partyId;
  if (answer.groupInstanceId !== undefined) {
    out.groupInstanceId = answer.groupInstanceId;
  }
  return out;
};

const deserializeAnswer = (raw: SerializedAnswer): Answer => {
  const out: Answer = {
    applicationId: raw.applicationId,
    fieldId: raw.fieldId as FieldId,
    value: raw.value,
    schemaVersion: raw.schemaVersion,
    updatedAt: raw.updatedAt,
    updatedBy: raw.updatedBy,
  };
  if (raw.partyId !== undefined) {
    (out as { partyId?: string }).partyId = raw.partyId;
  }
  if (raw.groupInstanceId !== undefined) {
    (out as { groupInstanceId?: string }).groupInstanceId = raw.groupInstanceId;
  }
  return out;
};

const serializeAnswers = (
  answers: readonly Answer[],
): Record<string, SerializedAnswer> => {
  const map: Record<string, SerializedAnswer> = {};
  for (const answer of answers) {
    map[fieldKeyOf(answer)] = serializeAnswer(answer);
  }
  return map;
};

const deserializeAnswers = (
  map: Record<string, SerializedAnswer> | undefined,
): readonly Answer[] => {
  if (!map) return [];
  const out: Answer[] = [];
  // Sort keys so the round-trip array order is deterministic; the
  // engine treats the array as a set, but tests appreciate stability.
  const keys = Object.keys(map).sort();
  for (const k of keys) {
    out.push(deserializeAnswer(map[k]!));
  }
  return out;
};

const docToSnapshot = (
  data: SerializedApplicationDoc | undefined,
): PersistedSnapshot | null => {
  if (!data) return null;
  return {
    schemaVersion: data.schemaVersion,
    savedAt: data.savedAt,
    answers: deserializeAnswers(data.answers),
  };
};

export interface FirestorePersistenceAdapter extends AnswerPersistenceAdapter {
  /**
   * Returns the underlying `DocumentReference` for callers that want to
   * compose Firestore operations alongside the adapter (e.g., the
   * linkage layer in Slice 4).
   */
  applicationDocRef: (applicationId: string) => DocumentReference<DocumentData>;
}

/**
 * Builds a Firestore-backed `AnswerPersistenceAdapter` against the
 * supplied `Firestore` instance. The adapter does not own SDK
 * initialization — `getFirestore(app)` is the caller's responsibility.
 */
export const createFirestorePersistenceAdapter = (
  firestore: Firestore,
): FirestorePersistenceAdapter => {
  const applicationDocRef = (
    applicationId: string,
  ): DocumentReference<DocumentData> =>
    doc(firestore, APPLICATIONS_COLLECTION, applicationId);

  const load = async (
    applicationId: string,
    options?: LoadOptions,
  ): Promise<PersistedLoadResult> => {
    const ref = applicationDocRef(applicationId);
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return { snapshot: null, warnings: [] };
    }
    const data = docSnap.data() as SerializedApplicationDoc;
    const snapshot = docToSnapshot(data);
    const warnings: LoadWarning[] = [];
    if (
      snapshot !== null &&
      options?.expectedSchemaVersion !== undefined &&
      options.expectedSchemaVersion !== snapshot.schemaVersion
    ) {
      warnings.push({
        code: 'schema_version_mismatch',
        message:
          `Persisted snapshot for application ${applicationId} was written ` +
          `at schemaVersion ${snapshot.schemaVersion}, but the engine ` +
          `expects ${options.expectedSchemaVersion}. The engine must ` +
          `decide whether to run a migration or reject the snapshot.`,
        found: snapshot.schemaVersion,
        expected: options.expectedSchemaVersion,
      });
    }
    return { snapshot, warnings };
  };

  const save = async (
    applicationId: string,
    snapshot: PersistedSnapshot,
  ): Promise<void> => {
    const ref = applicationDocRef(applicationId);
    // Read first to carry every existing top-level field forward
    // verbatim. The persistence adapter only owns the answers
    // snapshot (schemaVersion / savedAt / updatedAt / answers); every
    // other field belongs to a different writer: linkage owns
    // ownerUserId/createdAt/status, the admin owns packet metadata
    // (packetStoragePath/FileName/UploadedAt/UploadedByUid/SizeBytes)
    // and case state (caseState/caseStateChangedAt/submittedAt/
    // caseNotes). Stripping any of them via a bare setDoc replace
    // either trips the applicant-update branch of firestore.rules
    // (packet-* immutability => "Missing or insufficient permissions"
    // — Sentry JAVASCRIPT-REACT-D, 2026-05-26) or silently overwrites
    // admin state that the rule does not lock down. Spreading
    // existingData keeps every column present.
    const existing = await getDoc(ref);
    const existingData = existing.exists()
      ? (existing.data() as Record<string, unknown>)
      : undefined;

    const out: Record<string, unknown> = {
      ...(existingData ?? {}),
      schemaVersion: snapshot.schemaVersion,
      savedAt: snapshot.savedAt,
      updatedAt: new Date().toISOString(),
      answers: serializeAnswers(snapshot.answers),
    };
    // Default status on the create path (no existing doc); preserve
    // any status the admin or submit handler set otherwise.
    if (out['status'] === undefined) {
      out['status'] = 'draft';
    }
    await setDoc(ref, out);
  };

  const subscribe = (
    applicationId: string,
    listener: PersistenceListener,
  ): Unsubscribe => {
    const ref = applicationDocRef(applicationId);
    const unsub = onSnapshot(
      ref,
      (snap: DocumentSnapshot<DocumentData>) => {
        // Filter local-write echoes per Phase 5 plan risk note: a save
        // we just performed will fire onSnapshot once with
        // hasPendingWrites=true (local) and again with
        // hasPendingWrites=false (server-acked). We only want the
        // server-acked event so the engine doesn't re-hydrate from
        // its own writes.
        if (snap.metadata.hasPendingWrites) return;
        if (!snap.exists()) return;
        const data = snap.data() as SerializedApplicationDoc;
        const persisted = docToSnapshot(data);
        if (persisted === null) return;
        try {
          listener(persisted);
        } catch (err) {
          // Mirror the in-memory adapter's listener-isolation pattern:
          // a buggy listener should not break the Firestore listener.
          const error = err;
          queueMicrotask(() => {
            throw error;
          });
        }
      },
    );
    return unsub;
  };

  return {
    load,
    save,
    subscribe,
    applicationDocRef,
  };
};
